'use client';

import Link from 'next/link';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined';
import Navigation from '../../../components/Navigation';
import {
    DocAnswer,
    DocBeforeAfter,
    DocCode,
    DocEyebrow,
    DocMistakeList,
    DocRelated,
    DocSection,
    DocSubtitle,
    DocText,
    DocTitle,
} from '../../../components/DocArticle';
import { encodeBase64 } from '../../../utils/base64';

const escapedExampleUrl = (() => {
    const oldEncoded = encodeBase64(JSON.stringify({ 'a/b': { timeout: 30 } }, null, 2));
    const newEncoded = encodeBase64(JSON.stringify({ 'a/b': { timeout: 60 } }, null, 2));
    return `/?old=${oldEncoded}&new=${newEncoded}`;
})();

const mistakes = [
    {
        mistake: 'Forgetting the leading slash',
        fix: '"users/0/name" is not a valid pointer. Every non-empty pointer starts with /: "/users/0/name". The only pointer without one is the empty string, meaning the whole document.',
    },
    {
        mistake: 'Treating "/users/" and "/users" as the same thing',
        fix: 'A trailing slash adds an empty-string segment: "/users/" addresses the key "" inside users. "/users" addresses users itself.',
    },
    {
        mistake: 'Escaping in the wrong order when building a path',
        fix: 'Escape ~ as ~0 first, then / as ~1. If you escape the slash first you will re-escape the tilde you just introduced and corrupt the key.',
    },
    {
        mistake: 'Using JSONPath or dot notation',
        fix: '$.users[0].name and users.0.name are other languages. JSON Pointer only has slash-separated segments: /users/0/name.',
    },
    {
        mistake: 'Assuming keys are case-insensitive',
        fix: '/Name and /name are different pointers. Object keys must match exactly, character for character.',
    },
    {
        mistake: 'Reading with the "-" token',
        fix: '"-" names the slot after the last array element. It is valid only as the target of an add in JSON Patch - using it with remove, replace or test is an error.',
    },
];

export default function JsonPointerPage() {
    return (
        <>
            <Navigation activePage="docs" />
            <main>
                <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6 }, px: { xs: 2, sm: 3 } }}>
                    {/* Breadcrumb */}
                    <Box component="nav" sx={{ display: 'flex', mb: 4, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>
                        <Link href="/" style={{ textDecoration: 'none', color: 'var(--fg-muted)' }}>
                            <Typography component="span" sx={{ '&:hover': { color: 'var(--accent)' }, fontSize: '0.875rem' }}>Home</Typography>
                        </Link>
                        <Typography component="span" sx={{ mx: 1, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>/</Typography>
                        <Link href="/docs" style={{ textDecoration: 'none', color: 'var(--fg-muted)' }}>
                            <Typography component="span" sx={{ '&:hover': { color: 'var(--accent)' }, fontSize: '0.875rem' }}>Documentation</Typography>
                        </Link>
                        <Typography component="span" sx={{ mx: 1, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>/</Typography>
                        <Typography component="span" sx={{ color: 'var(--fg)', fontSize: '0.875rem' }}>JSON Pointer</Typography>
                    </Box>

                    <Box sx={{ maxWidth: '800px' }}>
                        <DocEyebrow>Documentation</DocEyebrow>
                        <DocTitle>JSON Pointer (RFC 6901): The Complete Guide</DocTitle>
                        <DocSubtitle>
                            Every <code>path</code> and <code>from</code> in a JSON Patch is a JSON Pointer. It is a small
                            syntax - slash-separated segments, two escape sequences, one special array token - but it
                            decides whether your patch hits the right value.
                        </DocSubtitle>

                        <DocAnswer>
                            A JSON Pointer is a string that identifies one value inside a JSON document: segments
                            separated by <code>/</code>, evaluated left to right. <code>/users/0/name</code> means: the
                            key <code>users</code>, then array index <code>0</code>, then the key <code>name</code>.
                            The empty string addresses the whole document.
                        </DocAnswer>

                        <DocSection>Anatomy of a pointer</DocSection>
                        <DocText>
                            Given this document:
                        </DocText>
                        <DocCode label="Document">{`{
  "users": [
    { "name": "Ana", "admin": true },
    { "name": "Ben", "admin": false }
  ]
}`}</DocCode>
                        <DocText>
                            Each pointer below resolves to exactly one value:
                        </DocText>
                        <DocCode label="Pointers">{`/users          -> the whole array
/users/0        -> { "name": "Ana", "admin": true }
/users/0/name   -> "Ana"
/users/1/admin  -> false
""              -> the entire document`}</DocCode>
                        <DocText>
                            Evaluation is strict and left to right: each segment must exist before the next one is
                            resolved, object keys must match exactly, and array segments must be valid indexes.
                        </DocText>

                        <DocSection>Objects and nested paths</DocSection>
                        <DocText>
                            Inside an object, a segment is a key - matched exactly, case included. Keys can contain
                            spaces, punctuation and unicode without any special handling; only <code>~</code> and{' '}
                            <code>/</code> need escaping (covered below). A segment that does not exist makes the whole
                            pointer fail - pointers never create intermediate structure, though JSON
                            Patch's <code>add</code> can create the final step of a path.
                        </DocText>

                        <DocSection>Array indexes</DocSection>
                        <DocText>
                            Inside an array, a segment is a zero-based index written in base 10 without leading
                            zeros: <code>/items/0</code> is the first element, <code>/items/01</code> is invalid. One
                            token is special: <code>-</code> names the position one past the last element. It is how
                            JSON Patch appends - <code>{'{ "op": "add", "path": "/items/-", "value": "x" }'}</code> -
                            and it is covered in depth in{' '}
                            <Link href="/docs/json-patch-array" style={{ color: 'var(--accent)' }}>JSON Patch and arrays</Link>.
                        </DocText>

                        <DocSection>The root pointer and empty keys</DocSection>
                        <DocText>
                            Two edge cases look similar but are opposites:
                        </DocText>
                        <Box component="ul" sx={{ color: 'var(--fg-muted)', lineHeight: 1.8, pl: 3, mb: 3 }}>
                            <li><strong>The empty string <code>""</code></strong> is the root pointer: it addresses the entire document. In JSON Patch, <code>add</code> or <code>replace</code> with <code>"path": ""</code> replaces the whole document.</li>
                            <li><strong>A single slash <code>"/"</code></strong> addresses the key that is itself an empty string at the root: <code>{'{ "": "this value" }'}</code>.</li>
                        </Box>

                        <DocSection id="escaping">Escaping: ~0 and ~1</DocSection>
                        <DocText>
                            Because <code>/</code> separates segments and <code>~</code> starts an escape, a key that
                            literally contains those characters must be escaped. RFC 6901 defines exactly two escapes:
                        </DocText>
                        <DocCode label="Escape rules">{`~  becomes  ~0
/  becomes  ~1`}</DocCode>
                        <DocText>
                            So a real key maps to its pointer segment like this:
                        </DocText>
                        <DocCode label="Key to pointer">{`key "a/b"     ->  /a~1b
key "m~n"     ->  /m~0n
key "a/b~c"   ->  /a~1b~0c
key "a~1b"    ->  /a~01b   (a literal tilde-one in the key)`}</DocCode>
                        <DocText>
                            When you <em>build</em> a path from a key, escape <code>~</code> first and <code>/</code>{' '}
                            second - otherwise you re-escape your own escape. When you <em>evaluate</em> a path, the
                            RFC reverses the order: turn <code>~1</code> back into <code>/</code> first, then{' '}
                            <code>~0</code> back into <code>~</code>.
                        </DocText>
                        <DocText>
                            A full example. To change <code>timeout</code> inside the key <code>"a/b"</code>:
                        </DocText>
                        <DocCode label="Patch">{`[
  { "op": "replace", "path": "/a~1b/timeout", "value": 60 }
]`}</DocCode>
                        <DocBeforeAfter
                            before={`{ "a/b": { "timeout": 30 } }`}
                            after={`{ "a/b": { "timeout": 60 } }`}
                        />
                        <DocText>
                            Note what stays unescaped: only the two characters above ever change. Dots, spaces,
                            brackets and quotes in keys are written as-is.
                        </DocText>

                        <DocSection>Pointers inside JSON Patch</DocSection>
                        <DocText>
                            JSON Patch (RFC 6902) uses pointers in two places: <code>path</code>, the target of every
                            operation, and <code>from</code>, the source of{' '}
                            <Link href="/operations/move" style={{ color: 'var(--accent)' }}>move</Link> and{' '}
                            <Link href="/operations/copy" style={{ color: 'var(--accent)' }}>copy</Link>. Both follow
                            the same rules on this page. You will also see pointers in HTTP API error responses and in
                            JSON Schema, which reuses the same syntax.
                        </DocText>
                        <DocText>
                            RFC 6901 also defines a URI fragment form (<code>#/users/0/name</code>) for putting pointers
                            in URLs, with percent-encoding on top. JSON Patch documents always use the plain string
                            form described here.
                        </DocText>

                        <DocSection>Common mistakes</DocSection>
                        <DocMistakeList items={mistakes} />

                        <DocSection>Reference</DocSection>
                        <DocText>
                            The syntax is defined by{' '}
                            <a href="https://datatracker.ietf.org/doc/html/rfc6901#section-3" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>RFC 6901, section 3</a>,
                            evaluation and the <code>-</code> token by{' '}
                            <a href="https://datatracker.ietf.org/doc/html/rfc6901#section-4" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>section 4</a>,
                            and the URI fragment form by{' '}
                            <a href="https://datatracker.ietf.org/doc/html/rfc6901#section-6" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>section 6</a>.
                            JSON Patch's use of pointers is in{' '}
                            <a href="https://datatracker.ietf.org/doc/html/rfc6902#section-4" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>RFC 6902, section 4</a>.
                        </DocText>

                        {/* CTA */}
                        <Box sx={{
                            mt: 6, p: 4, textAlign: 'center',
                            borderRadius: 'var(--radius-lg)',
                            background: 'var(--accent)', color: 'white',
                        }}>
                            <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 600, mb: 2 }}>
                                See an escaped pointer generated for you
                            </Typography>
                            <Typography sx={{ mb: 3, opacity: 0.9 }}>
                                Open the generator with a document whose key contains a slash - the patch it produces uses /a~1b automatically.
                            </Typography>
                            <Button
                                variant="contained"
                                size="large"
                                href={escapedExampleUrl}
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                    backgroundColor: 'white', color: 'var(--accent)', fontWeight: 600,
                                    px: 4, py: 1.5, textTransform: 'none',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                                }}
                            >
                                Generate a patch with an escaped key
                            </Button>
                        </Box>

                        <DocRelated links={[
                            { href: '/docs/json-patch-array', title: 'JSON Patch and arrays', description: 'Indexes, /-, shifting and reordering in depth' },
                            { href: '/operations/move', title: 'Move operation', description: 'Uses from + path, both JSON Pointers' },
                            { href: '/operations/add', title: 'Add operation', description: 'The operation that can create the final path step' },
                            { href: '/docs', title: 'Documentation', description: 'Back to the JSON Patch documentation hub' },
                        ]} />
                    </Box>
                </Container>
            </main>
        </>
    );
}
